import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { InboxItem } from '@/types/inbox';

/**
 * Phase 06 — read the open `np_missing_in_backoffice` inbox item (if any)
 * for a given ReportRequest. Used by the triage stepper to render a
 * banner explaining that the report was reverted from STARTED back to
 * NP_SELECTED because the worker's call to gtintel rejected the
 * delegation with one or more missing partners.
 *
 * Returns the single open item (or `null`) plus a `refetch` callback so
 * a successful re-Start can clear the banner via auto-resolve on the
 * backend + a fresh read here.
 */
export interface UseNpMissingBackofficeBannerReturn {
  item: InboxItem | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useNpMissingBackofficeBanner(
  reportRequestId: string | undefined
): UseNpMissingBackofficeBannerReturn {
  const [item, setItem] = useState<InboxItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!reportRequestId) {
      setItem(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.listInbox({
        kind: 'np_missing_in_backoffice',
        entityType: 'report_request',
        entityId: reportRequestId,
        state: 'open',
        limit: 1,
      });
      // No ETag is sent here, so a 304 can't happen; the guard keeps the type honest.
      const items = result === 'not-modified' ? [] : result.data.items;
      setItem(items[0] ?? null);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      setError(e);
      setItem(null);
    } finally {
      setIsLoading(false);
    }
  }, [reportRequestId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return { item, isLoading, error, refetch: fetchOnce };
}
